import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import * as Yup from "yup";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";


interface CreateScorePayload {
    Score: number;
    ScoreTypeId: number;
    Description: string;
    ScoreTypeName?: string;
    QualificationId: number;
    ApplicantId: string;
}
// interface ScoreTypeData{
//     Name: number;
//     ScoreTypeId: string;
// }

interface ScoreTypeResponse {
  StatusCode: number;
  Message: string;
  Data: ScoreTypeData[];
}

interface ScoreTypeData {
  ScoreTypeId: number;
  RouteId: number;
  Name: string;
}
interface ComponentProps {
    handleClose: () => void;
}

interface Qualification {
    id: number;
    QualificationId: number;
    QualificationName: string;
    z: number;
    CreatedBy: string;
    ModifiedBy: string;
    CreateDate: string;
    LastModified: string;
    ActiveStatus: boolean;
    IsApprovalRequired: boolean;
    LevelBy: number;
    ApprovalTypeId: number;
    IsDeleted: boolean;
}

interface QualificationResponse {
    StatusCode: number;
    Message: string;
    Data: Qualification[];
}

const CreateScoreContent: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const getAllScoreTypes = async (): Promise<ScoreTypeResponse> => {
        return (await apiCall().get(`/SetUp/GetAllScoreTypes`)).data;
    }

    const getAllQualification = async(): Promise<QualificationResponse> => {
        return (await apiCall().get('/SetUp/GetAllQualification')).data
    };

    const [getScoreTypesQuery, getQualificationsQuery] = useQueries({
        queries: [
            {queryKey: ['get-scoretype'], queryFn: getAllScoreTypes, retry: 1},
            {queryKey: ['get-all-qualification'], queryFn: getAllQualification, retry: 0}
        ]
    });
    
    const scoreTypeOption =
    getScoreTypesQuery?.data?.Data && getScoreTypesQuery?.data?.Data?.length > 0 ? (
        getScoreTypesQuery.data?.Data.map((scoretypedata : ScoreTypeData ) => (
                <option value={scoretypedata?.ScoreTypeId} key = {scoretypedata?.ScoreTypeId} >
                    {scoretypedata?.Name}
                </option>
            ))
        ) : (
            <option disabled>
                { getScoreTypesQuery.isLoading ? 'loading....' : getScoreTypesQuery.isError ? 'error': ''}
            </option>
    )

    const qualificationOption = getQualificationsQuery?.data?.Data && getQualificationsQuery?.data?.Data?.length > 0 ? (
        getQualificationsQuery?.data?.Data.map((data: Qualification) => (
            <option value={data?.id} key={data?.id}>
                {data?.QualificationName}
            </option>
        ))
    ) : (
        <option disabled>
            {getQualificationsQuery.isLoading ? 'loading...' : getQualificationsQuery.isError ? 'error' : ''}
        </option>
    )
        
    const createscore = async (payload: CreateScorePayload) => {
        return await apiCall().post(
        '/SetUp/CreateUpdateScore',
        payload
        );
    };

    const createScoreMutation = useMutation(
        ["createscore"],
        createscore,
        { retry: 0 }
    );

    const createScoreHandler = async (values: FormikValues, resetForm: () => void) => {     
        const findScoreType = getScoreTypesQuery?.data?.Data.find((data: ScoreTypeData) => data?.ScoreTypeId.toString() === values?.scoreType)

        const createScorePayload: CreateScorePayload = {
            Score: values?.score,
            ScoreTypeId: values?.scoreType,
            Description: values?.description,
            QualificationId:values?.qualification,
            ScoreTypeName: findScoreType?.Name,
            ApplicantId: ""
        };

        try {
        await createScoreMutation.mutateAsync(createScorePayload, {
            onSuccess: () => {    
                showNotification({
                    message: 'Score created successful',
                    type: 'success',
                });
                queryClient.refetchQueries(['get-AllScore']);
                handleClose();
            },
        });
        
        } catch (error: any) {
        showNotification({
            message: error?.response?.data?.Message || error?.message,
            type: "error",
        });
        }
    };

    const validationSchema = Yup.object().shape({
        score: Yup.number().typeError('Only numbers are allowed').required('Score is required'),
        scoreType:Yup.string().required('Score Type is required'),
        description: Yup.string().required('Description is required'),
    })

    const formik = useFormik<FormikValues>({
        initialValues: {
            scoreType: '',
            score: '',
            description: '',
        },
        onSubmit: (values, { resetForm }) => {
            createScoreHandler(values, resetForm);
        },
        validationSchema,
    });
   
    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='scoreType'
                        type='text'
                        as={Select}
                        label='Score Type'
                        options= {scoreTypeOption}
                        placeholder='Select'
                    />
                    <Field 
                        name='qualification'
                        type='text'
                        as={Select}
                        label='Qualification'
                        options= {qualificationOption}
                        placeholder='Select'
                    />
                    <Field 
                        name='score'
                        type='number'
                        as={Input}
                        label='Score'
                        placeholder='Score'
                    />
                     <Field 
                        name='description'
                        type='text'
                        as={CustomTextArea}
                        label='Description'
                        placeholder='Enter Description'
                    />
                                       
                </FormWrapper>
              
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton text='Cancel' onClick={handleClose}/>
                        <StyledButton 
                            disabled={createScoreMutation.isLoading} 
                            text={createScoreMutation.isLoading ? "Submitting..." : "Submit"}
                            onClick={formik.handleSubmit} 
                            type="submit"
                        />
                    </ButtonContainer>
                </ButtonWrapper>
              
            </form>
        </FormikProvider>
    )
};


export default CreateScoreContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

